// The support chat — ONE component pair for both surfaces (admin «پشتیبانی» page
// and the Mini App's support sheet), per the component-first rule.
//   <ChatThread>   — a messaging-app thread: day separators, bubbles with a tail,
//                    the time under each message, delivery ticks on your own.
//   <ChatComposer> — a text field that GROWS with its content (no resize handle)
//                    and a round icon-only send button beside it.
import { useEffect, useLayoutEffect, useRef } from "react";
import { SendHorizontal } from "lucide-react";
import { C, FONT } from "./design-system.js";

// Server timestamps are naive UTC (datetime.utcnow()); without the "Z" a browser
// would read them as LOCAL time and every message would be 3.5 hours off.
export function parseServerTime(v) {
  if (!v) return null;
  const s = String(v);
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + "Z");
}

const TZ = "Asia/Tehran";

function dayKey(d) {
  return d.toLocaleDateString("en-CA", { timeZone: TZ });   // YYYY-MM-DD in Tehran
}

function dayLabel(d, lang) {
  const today = new Date();
  const yest = new Date(Date.now() - 86400000);
  const fa = lang !== "en";
  if (dayKey(d) === dayKey(today)) return fa ? "امروز" : "Today";
  if (dayKey(d) === dayKey(yest)) return fa ? "دیروز" : "Yesterday";
  return d.toLocaleDateString(fa ? "fa-IR-u-ca-persian" : "en-US", {
    timeZone: TZ, weekday: "long", day: "numeric", month: "long",
    ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
}

function timeLabel(d, lang) {
  return d.toLocaleTimeString(lang === "en" ? "en-US" : "fa-IR", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
}

// `mine` = the sender_type that belongs to whoever is looking ("admin" on the
// admin page, "user" in the Mini App). Own messages sit on the LEFT in this RTL
// layout — the mirrored convention of every Persian messenger.
export function ChatThread({ messages = [], mine, lang = "fa", emptyText, failedText, style }) {
  const endRef = useRef(null);
  const last = messages.length ? messages[messages.length - 1].id : null;
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [last]);

  let prevDay = null;
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, padding: "6px 2px", ...style }}>
      {!messages.length && (
        <div style={{ color: C.textDim, textAlign: "center", margin: "auto", padding: 24, fontSize: 13 }}>{emptyText}</div>
      )}
      {messages.map(m => {
        const d = parseServerTime(m.created_at) || new Date();
        const k = dayKey(d);
        const sep = k !== prevDay;
        prevDay = k;
        const own = m.sender_type === mine;
        const failed = m.delivery_status === "failed";
        const tick = !own ? "" : failed ? "⚠" : m.delivery_status === "pending" ? "🕓" : "✓";
        return (
          <div key={m.id} style={{ display: "contents" }}>
            {sep && (
              <div style={{ alignSelf: "center", margin: "10px 0 6px", background: `${C.bgDeep}CC`, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 11.5, borderRadius: 999, padding: "3px 12px" }}>
                {dayLabel(d, lang)}
              </div>
            )}
            <div style={{
              alignSelf: own ? "flex-end" : "flex-start", maxWidth: "82%",
              background: own ? `${C.gold}22` : C.bgDeep,
              border: `1px solid ${failed ? C.red : own ? `${C.gold}40` : C.border}`,
              // a messenger "tail": the corner nearest the sender stays sharp
              borderRadius: own ? "16px 16px 16px 4px" : "16px 16px 4px 16px",
              padding: "8px 12px 5px", color: C.cream, fontSize: 14, lineHeight: 1.85,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              {m.body}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 5, marginTop: 2, color: failed ? C.red : C.textMid, fontSize: 10.5, lineHeight: 1.4 }}>
                {failed && <span>{failedText}{m.delivery_error ? `: ${m.delivery_error}` : ""}</span>}
                <span dir="ltr">{timeLabel(d, lang)}</span>
                {tick && <span>{tick}</span>}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

const MAX_H = 150;

export function ChatComposer({ value, onChange, onSend, busy, placeholder, maxLength = 4000, sendLabel = "ارسال" }) {
  const ref = useRef(null);
  // Grow with the content: reset to auto, then take the scroll height (capped —
  // past MAX_H the field scrolls inside instead of eating the thread).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, MAX_H) + "px";
    el.style.overflowY = el.scrollHeight > MAX_H ? "auto" : "hidden";
  }, [value]);

  const can = !!value.trim() && !busy;
  function onKey(e) {
    // Enter is ALWAYS a newline (messages are often multi-line); Ctrl+Enter
    // (⌘+Enter on a Mac) sends. On a phone the button sends.
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (can) onSend();
    }
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginTop: 10 }}>
      <textarea ref={ref} rows={1} value={value} maxLength={maxLength} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} onKeyDown={onKey}
        style={{ flex: 1, minWidth: 0, resize: "none", boxSizing: "border-box", background: C.bgDeep, color: C.cream,
          border: `1px solid ${C.border}`, borderRadius: 22, padding: "10px 16px", fontFamily: FONT, fontSize: 14.5,
          lineHeight: 1.7, outline: "none", maxHeight: MAX_H, overflowY: "hidden" }} />
      <button type="button" className="press" onClick={() => can && onSend()} disabled={!can} aria-label={sendLabel} title={`${sendLabel} (Ctrl+Enter)`}
        style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 999, border: "none",
          background: can ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : C.bgDeep,
          color: can ? C.bgDeep : C.textDim, display: "flex", alignItems: "center", justifyContent: "center",
          cursor: can ? "pointer" : "default", transition: "background 0.15s" }}>
        {/* the arrow points LEFT — "forward" in a right-to-left layout */}
        <SendHorizontal size={20} style={{ transform: "scaleX(-1)", opacity: busy ? 0.5 : 1 }} />
      </button>
    </div>
  );
}
